import type { VideoListItem, VideoUrgency } from '@ayub/shared'
import { LINK_VERIFY_SLA_HOURS, VideoStage } from '@ayub/shared'
import { formatRelativeUz } from '@/lib/relative-date'

/**
 * Shoshilinchlik ranglari — `docs/04-ui-ux.md` "Shoshilinchlik ranglari (tekshiruv muddati)":
 * kechikkan qizil, bugun amber, kelajakda so'nik (hint), yakunlangan yashil. `none` (muddat
 * umuman yo'q — masalan ko'rib chiqilmagan moderatsiya yozuvi) — neytral, belgisiz.
 *
 * ⚠️ Rang hech qachon yagona signal emas: har joyda shu ranglar matn bilan birga keladi
 * (`urgencyLabel()` pastda).
 */
export const VIDEO_URGENCY_META: Record<VideoUrgency, { dotClass: string; textClass: string }> = {
  overdue: { dotClass: 'bg-urgency-overdue', textClass: 'text-urgency-overdue-text' },
  today: { dotClass: 'bg-urgency-today', textClass: 'text-urgency-today-text' },
  upcoming: { dotClass: 'bg-urgency-upcoming', textClass: 'text-app-hint' },
  finalized: { dotClass: 'bg-urgency-done', textClass: 'text-urgency-done-text' },
  none: { dotClass: 'bg-app-hint/40', textClass: 'text-app-hint' },
}

/** Kanban saralashida ustunlik tartibi — kechikkan eng tepada, keyin bugun, keyin qolganlar. */
const URGENCY_RANK: Record<VideoUrgency, number> = {
  overdue: 0,
  today: 1,
  upcoming: 2,
  finalized: 3,
  none: 4,
}

/**
 * Kanban ustuni ichidagi tartib — `docs/03-app-flow.md` "Saralash: kechikkanlar eng tepada,
 * keyin bugungilar, keyin muddat bo'yicha". Barcha ustunlarga qo'llanadi (nafaqat PM
 * Tekshiruvda) — chunki Moderatsiya ustunidagi "havola kutilmoqda" kartochkalari ham xuddi
 * shu `urgency`/`nextCheckAt` juftligidan foydalanadi va kechikkanlari yuqoriga chiqishi kerak.
 */
export function sortVideosByUrgency<
  T extends { urgency: VideoUrgency; nextCheckAt: string | null },
>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const rankDiff = URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency]
    if (rankDiff !== 0) return rankDiff
    const aTime = a.nextCheckAt ? new Date(a.nextCheckAt).getTime() : Number.POSITIVE_INFINITY
    const bTime = b.nextCheckAt ? new Date(b.nextCheckAt).getTime() : Number.POSITIVE_INFINITY
    return aTime - bTime
  })
}

/** "3 kun kechikdi" / "Bugun" / "5 kundan keyin" / "Yakunlandi" — rang bilan birga yuriladigan matn. */
export function urgencyLabel(urgency: VideoUrgency, daysUntilCheck: number | null): string {
  switch (urgency) {
    case 'overdue': {
      const days = daysUntilCheck !== null ? Math.abs(daysUntilCheck) : null
      return days !== null ? `${days} kun kechikdi` : 'Kechikdi'
    }
    case 'today':
      return 'Bugun'
    case 'upcoming':
      return daysUntilCheck !== null ? `${daysUntilCheck} kundan keyin` : 'Kelajakda'
    case 'finalized':
      return 'Yakunlandi'
    case 'none':
    default:
      return ''
  }
}

/**
 * "Havola kutilmoqda" sub-holati — `docs/05-backend-schema.md`: "'tasdiqlangan, havola
 * kutilmoqda' alohida varonka emas — u `MODERATION` ichida `moderatedAt != null &&
 * postedAt == null` sharti bilan aniqlanadi".
 *
 * Dastlab bu holat `nextCheckAt` orqali BILVOSITA aniqlanardi, lekin bazadan tekshirilganda
 * taxmin noto'g'ri chiqdi: `MODERATION` bosqichidagi HAMMA videoda `nextCheckAt` bo'sh —
 * u faqat `POSTED` ga o'tganda to'ldiriladi. Ya'ni tekshiruv hech qachon `true` bermasdi va
 * admin allaqachon tasdiqlagan kartochkani qayta-qayta ochib ko'raverardi.
 * Shu sababli `moderatedAt` shartnomaga (`VideoListItem`) qo'shildi va bu yerda
 * to'g'ridan-to'g'ri ishlatiladi — taxminsiz.
 */
export function isAwaitingLink(video: Pick<VideoListItem, 'stage' | 'moderatedAt'>): boolean {
  return video.stage === VideoStage.MODERATION && video.moderatedAt !== null
}

const MS_PER_HOUR = 60 * 60 * 1000

/**
 * Havola tekshiruvi bo'yicha shoshilinchlik (Bosqich 8) — `docs/03-app-flow.md` "Havola
 * tekshiruvi (admin tomonida)": admin `LINK_VERIFY_SLA_HOURS` (48 soat, `@ayub/shared`)
 * ichida havolani ochib ko'rishi kutiladi, aks holda noto'g'ri havola 15-kungacha yotib
 * qolishi mumkin.
 *
 * `video.urgency` (server, `services/videos.ts`) BUNI QAMRAB OLMAYDI — u FAQAT `CHECKING`
 * bosqichida (tekshiruv muddati) hisoblanadi, `POSTED` bosqichida har doim `'none'`.
 * Shuning uchun bu funksiya MUSTAQIL, `postedAt`dan (havola kelgan vaqt = hisob boshlanishi)
 * hisoblaydi — `checkUrgency()`ga PARALLEL tizim EMAS, balki uning qamrab olmagan bo'shlig'ini
 * to'ldiradi va bir xil ikki bosqichli (qizil/amber) ranglash naqshini ishlatadi.
 */
export function linkVerifyUrgency(
  video: Pick<VideoListItem, 'linkVerified' | 'postedAt'>,
  now: Date = new Date(),
): 'overdue' | 'pending' | null {
  if (video.linkVerified) return null
  if (video.postedAt === null) return null
  const hoursSincePosted = (now.getTime() - new Date(video.postedAt).getTime()) / MS_PER_HOUR
  return hoursSincePosted >= LINK_VERIFY_SLA_HOURS ? 'overdue' : 'pending'
}

/** Kartochka/qatordagi bosqichga xos holat matni — kanban va list ko'rinishida umumiy. */
export function videoCardStatus(video: VideoListItem): { text: string; toneClass: string } {
  if (video.stage === VideoStage.MODERATION) {
    if (isAwaitingLink(video)) {
      const label = urgencyLabel(video.urgency, video.daysUntilCheck)
      return {
        text: label ? `Havola kutilmoqda · ${label}` : 'Havola kutilmoqda',
        toneClass: VIDEO_URGENCY_META[video.urgency].textClass,
      }
    }
    return { text: "Ko'rib chiqish kutilmoqda", toneClass: 'text-app-hint' }
  }

  if (video.stage === VideoStage.POSTED) {
    const base = video.postedAt ? `Joylandi · ${formatRelativeUz(video.postedAt)}` : 'Joylandi'
    const linkUrgency = linkVerifyUrgency(video)
    // 48 soatdan oshgan — qizil (shoshilinch), oshmagan — amber (belgilangan, lekin
    // hali shoshilinch emas). `docs/03`: "muddat o'tsa kartochka kunlik admin xabariga
    // va dashboard hisoblagichiga tushadi" — shuning uchun ikkalasi HAM bir xil emas.
    if (linkUrgency === 'overdue') {
      return {
        text: `${base} · havola tekshirilmagan (48s+)`,
        toneClass: 'text-urgency-overdue-text',
      }
    }
    if (linkUrgency === 'pending') {
      return { text: `${base} · havola tekshirilmagan`, toneClass: 'text-urgency-today-text' }
    }
    return { text: base, toneClass: 'text-app-hint' }
  }

  if (video.stage === VideoStage.CHECKING) {
    if (video.urgency === 'finalized') {
      return {
        text: "Yakunlandi · to'lov kutilmoqda",
        toneClass: VIDEO_URGENCY_META.finalized.textClass,
      }
    }
    const checkLabel = 'Tekshiruv'
    const label = urgencyLabel(video.urgency, video.daysUntilCheck)
    // Havola CHECKING bosqichigacha (15 kun) tekshirilmasdan qolishi mumkin — bu holat
    // oddiy tekshiruv muddatidan ham shoshilinchroq, shuning uchun ustunlik beriladi
    // (docs/03: aynan shu holatning oldini olish uchun 48 soatlik SLA bor).
    if (linkVerifyUrgency(video) === 'overdue') {
      return {
        text: `${checkLabel} · havola tekshirilmagan!`,
        toneClass: VIDEO_URGENCY_META.overdue.textClass,
      }
    }
    return {
      text: label ? `${checkLabel} · ${label}` : checkLabel,
      toneClass: VIDEO_URGENCY_META[video.urgency].textClass,
    }
  }

  if (video.stage === VideoStage.PAID) {
    return { text: "To'landi", toneClass: 'text-stage-paid-text' }
  }

  return { text: 'Rad etildi', toneClass: 'text-stage-rejected-text' }
}
